import { useState } from "react";
import { useCstIcmsCrud, useCsosnCrud } from "@/features/fiscal/hooks/useFiscal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { SacFiscalSyncButton } from "@/components/SacFiscalSyncButton";
import { type TamanhoPagina } from "@/hooks/usePaginacaoLocal";
import type { CstIcmsDto, SalvarCstIcmsRequest, CsosnDto, SalvarCsosnRequest } from "@/features/fiscal/types";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Search, FileCode, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { AvisoTabelaNacional } from "@/components/AvisoTabelaNacional";

// ── Constantes ────────────────────────────────────────────────────────────

const TIPO_CST_OPTIONS = [
  { value: "0", label: "0 — ICMS Regime Normal" },
  { value: "1", label: "1 — IBS/CBS (Reforma Tributária)" },
];

const EMPTY_CST: SalvarCstIcmsRequest = {
  cst: "",
  descricao: "",
  tipoCst: 0,
  indIbsCbs: false,
  indIbsCbsMono: false,
  indReducao: false,
  indDiferimento: false,
  indTransferenciaCredito: false,
  indCredPresIbsZfm: false,
  indAjusteCompetencia: false,
  indRedutorBc: false,
};

const EMPTY_CSOSN: SalvarCsosnRequest = {
  codigoCsosn: 0,
  descricao: "",
  textoExplicativo: "",
};

// ── Seção CST ICMS ────────────────────────────────────────────────────────

function CstIcmsTab() {
  const { user } = useAuth();
  const podeEditar = user?.isAdminGlobal ?? false;
  const [busca, setBusca]           = useState("");
  const [pagina, setPagina]         = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId]     = useState<number | null>(null);
  const [editId, setEditId]         = useState<number | null>(null);
  const [form, setForm]             = useState<SalvarCstIcmsRequest>(EMPTY_CST);
  const [saving, setSaving]         = useState(false);

  const { query, obterPorId, criar, atualizar, excluir } = useCstIcmsCrud(busca || undefined, pagina, tamanhoPagina);
  const itens = query.data?.itens ?? [];

  const totalItens = query.data?.total ?? 0;
  const primeiroItem = totalItens === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const ultimoItem = Math.min(pagina * tamanhoPagina, totalItens);

  const set = (field: keyof SalvarCstIcmsRequest, value: unknown) =>
    setForm(f => ({ ...f, [field]: value }));

  const openNew = () => {
    setEditId(null);
    setForm(EMPTY_CST);
    setDialogOpen(true);
  };

  const openEdit = async (id: number) => {
    try {
      const dto: CstIcmsDto = await obterPorId(id);
      setEditId(id);
      setForm({
        cst:                     dto.cst ?? "",
        descricao:               dto.descricao ?? "",
        tipoCst:                 dto.tipoCst,
        indIbsCbs:               dto.indIbsCbs,
        indIbsCbsMono:           dto.indIbsCbsMono,
        indReducao:              dto.indReducao,
        indDiferimento:          dto.indDiferimento,
        indTransferenciaCredito: dto.indTransferenciaCredito,
        indCredPresIbsZfm:       dto.indCredPresIbsZfm,
        indAjusteCompetencia:    dto.indAjusteCompetencia,
        indRedutorBc:            dto.indRedutorBc,
      });
      setDialogOpen(true);
    } catch {
      toast.error("Erro ao carregar CST ICMS.");
    }
  };

  const handleSave = async () => {
    if (!form.cst?.trim()) { toast.error("CST é obrigatório."); return; }
    setSaving(true);
    try {
      if (editId) {
        await atualizar.mutateAsync({ id: editId, req: form });
        toast.success("CST ICMS atualizado.");
      } else {
        await criar.mutateAsync(form);
        toast.success("CST ICMS criado.");
      }
      setDialogOpen(false);
    } catch {
      toast.error("Erro ao salvar CST ICMS.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    try {
      await excluir.mutateAsync(deleteId);
      toast.success("CST ICMS excluído.");
    } catch {
      toast.error("Erro ao excluir CST ICMS.");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Barra superior */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por código ou descrição..."
            className="pl-9"
            value={busca}
            onChange={e => { setBusca(e.target.value); setPagina(1); }}
          />
        </div>
        <div className="flex gap-2">
          {podeEditar && (
            <SacFiscalSyncButton
              endpoint="/api/cadastros/fiscal/sacfiscal/sincronizar/cst-cbs-ibs"
              label="Sincronizar IBS/CBS"
              onDone={() => query.refetch()}
            />
          )}
          {podeEditar && (
            <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" /> Novo CST</Button>
          )}
        </div>
      </div>

      {/* Tabela */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[90px]">CST</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="w-[200px]">Tipo CST</TableHead>
              <TableHead className="w-[80px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : itens.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Nenhum CST encontrado.</TableCell></TableRow>
            ) : itens.map(c => (
              <TableRow key={c.id}>
                <TableCell className="font-mono font-semibold">{c.cst ?? "—"}</TableCell>
                <TableCell className="text-sm">{c.descricao ?? "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {c.tipoCst === 0 ? "ICMS Regime Normal" : c.tipoCst === 1 ? "IBS/CBS (Reforma Tributária)" : `Tipo ${c.tipoCst}`}
                </TableCell>
                <TableCell>
                  {podeEditar && (
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(c.id)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(c.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      {query.data && (
        <PaginacaoRodape
          pagina={pagina}
          totalPaginas={query.data.totalPaginas}
          totalItens={totalItens}
          primeiroItem={primeiroItem}
          ultimoItem={ultimoItem}
          tamanhoPagina={tamanhoPagina}
          onPaginaChange={setPagina}
          onTamanhoPaginaChange={setTamanhoPagina}
        />
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editId ? "Editar CST ICMS" : "Novo CST ICMS"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1">
              <Label>CST <span className="text-destructive">*</span></Label>
              <Input value={form.cst ?? ""} onChange={e => set("cst", e.target.value)} maxLength={10} placeholder="Ex.: 00" />
            </div>
            <div className="space-y-1">
              <Label>Tipo CST</Label>
              <Select value={String(form.tipoCst)} onValueChange={v => set("tipoCst", Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPO_CST_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1">
              <Label>Descrição</Label>
              <Input value={form.descricao ?? ""} onChange={e => set("descricao", e.target.value)} maxLength={200} />
            </div>
            <div className="col-span-2">
              <p className="text-sm font-medium mb-2">Indicadores IBS/CBS</p>
              <div className="grid grid-cols-2 gap-2">
                {([
                  ["indIbsCbs",               "IBS/CBS"],
                  ["indIbsCbsMono",            "IBS/CBS Monofásico"],
                  ["indReducao",               "Redução"],
                  ["indDiferimento",           "Diferimento"],
                  ["indTransferenciaCredito",  "Transferência de Crédito"],
                  ["indCredPresIbsZfm",        "Créd. Presumido IBS ZFM"],
                  ["indAjusteCompetencia",     "Ajuste de Competência"],
                  ["indRedutorBc",             "Redutor de BC"],
                ] as [keyof SalvarCstIcmsRequest, string][]).map(([field, label]) => (
                  <div key={field} className="flex items-center space-x-2">
                    <Checkbox
                      id={field}
                      checked={!!form[field]}
                      onCheckedChange={v => set(field, !!v)}
                    />
                    <label htmlFor={field} className="text-sm cursor-pointer">{label}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm excluir */}
      <AlertDialog open={deleteId !== null} onOpenChange={open => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir CST ICMS?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Seção CSOSN ───────────────────────────────────────────────────────────

function CsosnTab() {
  const { user } = useAuth();
  const podeEditar = user?.isAdminGlobal ?? false;
  const [busca, setBusca]           = useState("");
  const [pagina, setPagina]         = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId]     = useState<number | null>(null);
  const [editId, setEditId]         = useState<number | null>(null);
  const [form, setForm]             = useState<SalvarCsosnRequest>(EMPTY_CSOSN);
  const [saving, setSaving]         = useState(false);

  const { query, obterPorId, criar, atualizar, excluir } = useCsosnCrud(busca || undefined, pagina, tamanhoPagina);
  const itens = query.data?.itens ?? [];

  const totalItens = query.data?.total ?? 0;
  const primeiroItem = totalItens === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const ultimoItem = Math.min(pagina * tamanhoPagina, totalItens);

  const set = (field: keyof SalvarCsosnRequest, value: unknown) =>
    setForm(f => ({ ...f, [field]: value }));

  const openNew = () => { setEditId(null); setForm(EMPTY_CSOSN); setDialogOpen(true); };

  const openEdit = async (id: number) => {
    try {
      const dto: CsosnDto = await obterPorId(id);
      setEditId(id);
      setForm({
        codigoCsosn:      dto.codigoCsosn,
        descricao:        dto.descricao ?? "",
        textoExplicativo: dto.textoExplicativo ?? "",
      });
      setDialogOpen(true);
    } catch {
      toast.error("Erro ao carregar CSOSN.");
    }
  };

  const handleSave = async () => {
    if (!form.descricao?.trim()) { toast.error("Descrição é obrigatória."); return; }
    setSaving(true);
    try {
      if (editId) {
        await atualizar.mutateAsync({ id: editId, req: form });
        toast.success("CSOSN atualizado.");
      } else {
        await criar.mutateAsync(form);
        toast.success("CSOSN criado.");
      }
      setDialogOpen(false);
    } catch {
      toast.error("Erro ao salvar CSOSN.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    try {
      await excluir.mutateAsync(deleteId);
      toast.success("CSOSN excluído.");
    } catch {
      toast.error("Erro ao excluir CSOSN.");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por descrição..."
            className="pl-9"
            value={busca}
            onChange={e => { setBusca(e.target.value); setPagina(1); }}
          />
        </div>
        {podeEditar && (
          <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" /> Novo CSOSN</Button>
        )}
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Código CSOSN</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="w-[80px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : itens.length === 0 ? (
              <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">Nenhum CSOSN encontrado.</TableCell></TableRow>
            ) : itens.map(c => (
              <TableRow key={c.id}>
                <TableCell className="font-mono font-semibold">{c.codigoCsosn}</TableCell>
                <TableCell className="text-sm">{c.descricao ?? "—"}</TableCell>
                <TableCell>
                  {podeEditar && (
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(c.id)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(c.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {query.data && (
        <PaginacaoRodape
          pagina={pagina}
          totalPaginas={query.data.totalPaginas}
          totalItens={totalItens}
          primeiroItem={primeiroItem}
          ultimoItem={ultimoItem}
          tamanhoPagina={tamanhoPagina}
          onPaginaChange={setPagina}
          onTamanhoPaginaChange={setTamanhoPagina}
        />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Editar CSOSN" : "Novo CSOSN"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Código CSOSN <span className="text-destructive">*</span></Label>
              <Input
                type="number"
                value={form.codigoCsosn || ""}
                onChange={e => set("codigoCsosn", Number(e.target.value))}
              />
            </div>
            <div className="space-y-1">
              <Label>Descrição <span className="text-destructive">*</span></Label>
              <Input value={form.descricao ?? ""} onChange={e => set("descricao", e.target.value)} maxLength={200} />
            </div>
            <div className="space-y-1">
              <Label>Texto Explicativo</Label>
              <Textarea value={form.textoExplicativo ?? ""} onChange={e => set("textoExplicativo", e.target.value)} maxLength={400} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteId !== null} onOpenChange={open => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir CSOSN?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────

export default function CstIcms() {
  const { user } = useAuth();
  const podeEditar = user?.isAdminGlobal ?? false;
  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileCode className="h-6 w-6" /> CST ICMS / CSOSN
        </h1>
        <p className="text-muted-foreground">Situação Tributária — ICMS e Simples Nacional</p>
      </div>

      {!podeEditar && <AvisoTabelaNacional />}

      <Tabs defaultValue="cst-icms">
        <TabsList>
          <TabsTrigger value="cst-icms">CST ICMS</TabsTrigger>
          <TabsTrigger value="csosn">CSOSN (Simples Nacional)</TabsTrigger>
        </TabsList>
        <TabsContent value="cst-icms" className="mt-4">
          <CstIcmsTab />
        </TabsContent>
        <TabsContent value="csosn" className="mt-4">
          <CsosnTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
